import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, ChevronRight, ChevronDown, ShieldCheck, MessageCircle, CreditCard, User } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../context/SiteContext";
import { useReferral } from "../context/ReferralContext";
import { openWhatsapp } from "../lib/whatsapp";
import { CategoryIllustration } from "./CategoryIllustration";

export function Navbar() {
  const { settings, categories } = useSite();
  const { referralCode, referralData } = useReferral();
  const [open, setOpen] = useState(false);

  const phoneRaw = settings.phone_number || settings.whatsapp_number || "";
  const phoneHref = `tel:${phoneRaw.replace(/[^0-9+]/g, "")}`;

  const requestConsulenza = async () => {
    setOpen(false);
    const res = await openWhatsapp({
      number: settings.whatsapp_number,
      product: "una consulenza gratuita",
      referralCode,
      discount: referralData?.discount_value,
      bonusType: referralData?.bonus_type,
    });
    if (!res.ok && res.reason === "no_number") {
      toast.error("Numero WhatsApp non ancora configurato dall'amministratore.");
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white">
      {/* Top utility bar */}
      <div className="bg-navy text-white text-xs">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-9 flex items-center justify-between">
          <div className="flex items-center gap-2 font-medium">
            <ShieldCheck className="w-3.5 h-3.5 text-brand" />
            <span className="hidden sm:inline">Servizio gratuito, trasparente e senza impegno</span>
            <span className="sm:hidden">Servizio gratuito</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/area-clienti" data-testid="topbar-customer" className="flex items-center gap-1.5 font-semibold hover:text-brand transition-colors">
              <User className="w-3.5 h-3.5 text-brand" /> Area Clienti
            </Link>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-28 flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo" className="shrink-0 flex items-center">
            <img src="/logo-enertel.png" alt={settings.company_name || "Enertel Solutions"} className="h-24 w-auto" />
          </Link>

          {/* Desktop nav with dropdowns */}
          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {categories.map((c) => (
              <div key={c.slug} className="relative group">
                <Link
                  to={`/servizi/${c.slug}`}
                  data-testid={`nav-link-${c.slug}`}
                  className="flex items-center gap-1 px-3 py-2 text-[15px] font-semibold rounded-lg text-navy group-hover:text-brand-dark group-hover:bg-surface transition-colors"
                >
                  {c.name}
                  <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                </Link>

                <div className="absolute left-0 top-full pt-2 w-80 z-50 hidden group-hover:block" data-testid={`nav-dropdown-${c.slug}`}>
                  <div className="bg-white rounded-2xl border border-line soft-shadow-lg p-5 animate-fade-up">
                    <div className="flex items-center gap-3">
                      <CategoryIllustration icon={c.icon} className="w-11 h-11" />
                      <div className="font-heading font-bold text-navy">{c.name}</div>
                    </div>

                    {c.subcategories && c.subcategories.length > 0 ? (
                      <div className="mt-4 grid grid-cols-1 gap-0.5">
                        {c.subcategories.map((sub) => (
                          (c.segments && c.segments.length > 0) ? (
                            <div key={sub} className="relative group/sub">
                              <Link
                                to={`/servizi/${c.slug}?sub=${encodeURIComponent(sub)}`}
                                data-testid={`nav-sub-${c.slug}-${sub}`}
                                className="flex items-center justify-between px-3 py-2 rounded-lg text-sm font-semibold text-navy hover:bg-surface hover:text-brand-dark transition-colors"
                              >
                                {sub}
                                <ChevronRight className="w-4 h-4 text-line group-hover/sub:text-brand transition-colors" />
                              </Link>
                              <div className="absolute left-full top-0 pl-2 w-52 hidden group-hover/sub:block z-50">
                                <div className="bg-white rounded-xl border border-line soft-shadow-lg p-2 animate-fade-up">
                                  {c.segments.map((seg) => (
                                    <Link
                                      key={seg}
                                      to={`/servizi/${c.slug}?sub=${encodeURIComponent(sub)}&seg=${encodeURIComponent(seg)}`}
                                      data-testid={`nav-seg-${c.slug}-${sub}-${seg}`}
                                      className="flex items-center justify-between px-3 py-2 rounded-lg text-sm font-semibold text-navy hover:bg-surface hover:text-brand-dark transition-colors"
                                    >
                                      {seg}
                                      <ChevronRight className="w-3.5 h-3.5 text-line" />
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <Link
                              key={sub}
                              to={`/servizi/${c.slug}?sub=${encodeURIComponent(sub)}`}
                              data-testid={`nav-sub-${c.slug}-${sub}`}
                              className="flex items-center justify-between px-3 py-2 rounded-lg text-sm font-semibold text-navy hover:bg-surface hover:text-brand-dark transition-colors group/sub"
                            >
                              {sub}
                              <ChevronRight className="w-4 h-4 text-line group-hover/sub:text-brand transition-colors" />
                            </Link>
                          )
                        ))}
                      </div>
                    ) : (
                      <p className="mt-3 text-sm text-navy-soft leading-relaxed">{c.description}</p>
                    )}

                    <Link
                      to={`/servizi/${c.slug}`}
                      className="mt-4 pt-3 border-t border-line inline-flex items-center gap-1.5 text-brand-dark font-bold text-sm hover:gap-2.5 transition-all"
                    >
                      Vai a tutte le offerte <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="hidden lg:flex items-center gap-5 shrink-0">
            <a
              href={phoneRaw ? phoneHref : "#contatti"}
              data-testid="nav-phone"
              className="flex items-center gap-2.5 group"
            >
              <span className="w-9 h-9 rounded-full bg-brand-light text-brand-darker flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
              </span>
              <span className="leading-tight">
                <span className="block text-[11px] text-navy-soft font-medium">Chiamaci ora</span>
                <span className="block text-sm font-bold text-navy">Parla con noi</span>
              </span>
            </a>
            <button
              type="button"
              onClick={requestConsulenza}
              data-testid="nav-cta"
              className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors soft-shadow"
            >
              Richiedi consulenza <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <button
            className="lg:hidden text-navy"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-b border-line bg-white px-5 py-4 flex flex-col gap-1">
          {categories.map((c) => (
            <div key={c.slug} className="py-1.5">
              <Link
                to={`/servizi/${c.slug}`}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${c.slug}`}
                className="flex items-center justify-between text-[15px] font-semibold text-navy py-1"
              >
                <span className="flex items-center gap-3">
                  <CategoryIllustration icon={c.icon} className="w-9 h-9" />
                  {c.name}
                </span>
                <ChevronRight className="w-4 h-4 text-brand" />
              </Link>
              {c.subcategories && c.subcategories.length > 0 && (
                <div className="ml-12 mt-1 space-y-2">
                  {c.subcategories.map((sub) => (
                    (c.segments && c.segments.length > 0) ? (
                      <div key={sub}>
                        <div className="text-xs font-bold text-navy">{sub}</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {c.segments.map((seg) => (
                            <Link
                              key={seg}
                              to={`/servizi/${c.slug}?sub=${encodeURIComponent(sub)}&seg=${encodeURIComponent(seg)}`}
                              onClick={() => setOpen(false)}
                              className="text-xs font-semibold text-navy-soft bg-surface rounded-full px-3 py-1.5 hover:text-brand-dark transition-colors"
                            >
                              {seg}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={sub}
                        to={`/servizi/${c.slug}?sub=${encodeURIComponent(sub)}`}
                        onClick={() => setOpen(false)}
                        className="inline-block text-xs font-semibold text-navy-soft bg-surface rounded-full px-3 py-1.5 mr-1.5 hover:text-brand-dark transition-colors"
                      >
                        {sub}
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link
            to="/carta-fedelta"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-fidelity"
            className="mt-2 flex items-center gap-3 text-[15px] font-semibold text-navy py-2.5"
          >
            <span className="w-9 h-9 rounded-lg bg-brand-light text-brand-darker flex items-center justify-center">
              <CreditCard className="w-5 h-5" />
            </span>
            Carta Fedeltà
          </Link>
          <Link
            to="/area-clienti"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-customer"
            className="flex items-center gap-3 text-[15px] font-semibold text-navy py-2.5"
          >
            <span className="w-9 h-9 rounded-lg bg-brand-light text-brand-darker flex items-center justify-center">
              <User className="w-5 h-5" />
            </span>
            Area Clienti
          </Link>
          <a
            href={phoneRaw ? phoneHref : "#contatti"}
            onClick={() => setOpen(false)}
            className="mt-2 flex items-center gap-2 text-sm font-bold text-navy py-2.5"
          >
            <Phone className="w-4 h-4 text-brand-dark" /> Parla con un esperto
          </a>
          <button
            type="button"
            onClick={requestConsulenza}
            data-testid="nav-mobile-cta"
            className="mt-1 inline-flex items-center justify-center gap-1.5 bg-brand hover:bg-brand-dark text-white text-sm font-bold px-5 py-3 rounded-full transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> Richiedi consulenza
          </button>
        </div>
      )}
    </header>
  );
}
